import React, { useState, useEffect, useCallback } from 'react';
import { auth, provider } from './firebase';
import { signInWithPopup } from 'firebase/auth';

// Helper component for the status bar (to avoid repetition)
const StaticConnectedBar = () => (
  <div style={{
    backgroundColor: '#E0F7FA', // Light blue background
    padding: '8px 20px',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #B0BEC5' // Subtle border
  }}>
    <div style={{
      width: '10px',
      height: '10px',
      backgroundColor: '#4CAF50', // Green circle
      borderRadius: '50%',
      marginRight: '8px'
    }}></div>
    <div style={{
      backgroundColor: 'white',
      color: '#37474F', // Darker grey text
      padding: '4px 12px',
      borderRadius: '12px', // Pill shape
      fontSize: '12px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)' // Subtle shadow
    }}>
      Paper Account Connected
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  // Default page is now 'login' if not authenticated
  const [page, setPage] = useState("login");
  const [selectedOrderType, setSelectedOrderType] = useState(null); // 'CALL' or 'PUT'
  const [selectedContracts, setSelectedContracts] = useState(1);
  const [connected, setConnected] = useState(false); // Backend connection status
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [realizedPL, setRealizedPL] = useState(0); // Default to 0
  const [openPL, setOpenPL] = useState(0); // Default to 0
  const [openContracts, setOpenContracts] = useState(0); // Default to 0
  const [lastOrderType, setLastOrderType] = useState(null); // Store the type of the last placed order
  const [email, setEmail] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.taptotrade.ai/api";

  const handleConnect = useCallback(async () => {
    setLoading(true);
    setConnected(false);
    try {
        if (!auth.currentUser) {
            // Cannot alert here reliably before component mounts fully after login/refresh
            // Consider setting an error state instead if needed immediately
            console.error("User not authenticated for handleConnect");
            setLoading(false);
            return;
        }
        const token = await auth.currentUser.getIdToken();

        // 1. Check Broker Connection
        const connectRes = await fetch(`${API_BASE_URL}/connect`, {
            method: "GET",
            headers: { "Authorization": `Bearer ${token}` }
        });

        if (!connectRes.ok) {
            const errorData = await connectRes.text();
            console.error(`Failed to check broker connection: ${connectRes.status} ${errorData}`);
            // alert(`Failed to check broker connection: ${connectRes.status} ${errorData}`);
            setLoading(false);
            return; // Stop if connection check fails
        }

        const connectData = await connectRes.json();
        if (connectData.status !== 'connected') {
            // alert("Broker is not connected.");
            console.warn("Broker is not connected."); // Use console for auto-connect attempt
            setLoading(false);
            return; // Stop if broker isn't connected
        }

        // Broker is connected!
        setConnected(true);
        console.log("Broker connected, fetching initial trade status...");

        // 2. Fetch Initial Trade Status
        const statusRes = await fetch(`${API_BASE_URL}/trade_status`, {
            method: "GET",
            headers: { "Authorization": `Bearer ${token}` }
        });

        if (statusRes.ok) {
            const statusData = await statusRes.json();
            setOpenContracts(statusData.openContracts ?? 0);
            setOpenPL(statusData.openPL ?? 0);
            setRealizedPL(statusData.realizedPL ?? 0);
            // Use the orderType from status if available and position is open
            if (statusData.openContracts > 0) {
                setLastOrderType(statusData.orderType === 'C' ? 'CALL' : (statusData.orderType === 'P' ? 'PUT' : null));
                console.log(`Found ${statusData.openContracts} open contracts. Navigating to status page.`);
                setPage("status"); // Go direct to status if position exists
            } else {
                setLastOrderType(null); // Clear last order type if no position
                console.log("No open contracts found. Navigating to trade page.");
                setSelectedOrderType(null);
                setPage("trade"); // Go to trade page if no position
            }
        } else {
            const errorText = await statusRes.text();
            console.error(`Failed to fetch initial trade status: ${statusRes.status} - ${errorText}`);
            // Fallback: Go to trade page even if status fails after connect
            // alert("Connected to broker, but failed to get initial trade status. Proceeding to trade page.");
            setSelectedOrderType(null);
            setPage("trade");
        }

    } catch (err) {
        console.error("Error during connection process:", err);
        // alert("Error during connection process.");
        setConnected(false); // Ensure connected is false on error
    } finally { // Make sure loading is set to false regardless of success/error
        setLoading(false);
    }
  // Add dependencies for handleConnect's useCallback
  // It uses API_BASE_URL (constant), setLoading, setConnected, setPage, setSelectedOrderType,
  // setOpenContracts, setOpenPL, setRealizedPL, setLastOrderType. Auth is checked internally.
  // We only list state setters and other variables/props from component scope. API_BASE_URL is stable.
  // Alert was removed to avoid issues during initial load.
  }, []); // Added lastOrderType as fetchTradeStatus depends on it indirectly now via setPage

  // Effect to check auth state and set initial page
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser?.email === "darrenbowman98@gmail.com") {
        setUser(currentUser);
        // Only run connect if user is set and we are not already connected/loading/on relevant page
        if (!connected && !loading && page !== 'trade' && page !== 'status') {
             handleConnect(); // Call the memoized version
        }
      } else {
        // ... logout logic ...
      }
    });
    return () => unsubscribe();
  // Add handleConnect (now memoized) to dependencies
  }, [connected, loading, page, handleConnect]); // <-- Add handleConnect here

  const fetchTradeStatus = useCallback(async () => {
    if (!auth.currentUser || page !== 'status') return;
  
    // We removed setLoading from here as it's just polling
    try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/trade_status`, {
            method: "GET",
            headers: { "Authorization": `Bearer ${token}` }
        });
  
        if (res.ok) {
            const data = await res.json();
            setOpenContracts(data.openContracts);
            // Use optional chaining ?. in case data.openPL is missing, default to 0
            setOpenPL(data.openPL ?? 0);
            setRealizedPL(data.realizedPL ?? 0);
            setConnected(data.connectionStatus === 'connected');
             // Update lastOrderType based on fetched status if position is open
             if (data.openContracts > 0) {
                setLastOrderType(data.orderType === 'C' ? 'CALL' : (data.orderType === 'P' ? 'PUT' : null));
             } else if (data.openContracts === 0 && lastOrderType !== null) {
                // Reset if position just closed
                setLastOrderType(null);
             }
        } else {
             const errorText = await res.text();
             console.error(`Failed to fetch trade status: ${res.status} - ${errorText}`);
             // Consider resetting state or showing error on UI if status fails repeatedly
             // setOpenContracts(0);
             // setOpenPL(0);
             // setConnected(false);
        }
    } catch (err) {
        console.error("Error fetching trade status:", err);
        // Consider resetting state or showing error on UI
        // setOpenContracts(0);
        // setOpenPL(0);
        // setConnected(false);
    }
    // setLoading(false); // Removed setLoading call
  // Add dependencies used inside the useCallback function
  }, [page, lastOrderType]); // Dependency array for useCallback  

  // Effect for polling trade status
  useEffect(() => {
    let intervalId = null;
    if (page === 'status' && user) {
      const fetchAndRepeat = () => {
        if (document.visibilityState === 'visible') { // Only poll if tab is visible
          fetchTradeStatus();
        }
      }
      fetchAndRepeat(); // Initial fetch
      intervalId = setInterval(fetchAndRepeat, 5000); // Poll every 5 seconds
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [page, user, fetchTradeStatus]); // Rerun effect if page or user changes

  // --- Core Logic Functions (Unchanged) ---
  const signIn = async () => {
    console.log("Starting sign in process...");
    try {
      console.log("Attempting to show Google sign-in popup...");
      const result = await signInWithPopup(auth, provider);
      console.log("Sign in successful:", result.user.email);
    } catch (err) {
      console.error("Login failed - Full error:", err);
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);
    }
  };

  

   const handleSubmitOrder = async () => {
    if (!selectedOrderType) {
      alert("Please select CALL or PUT.");
      return;
    }

    setLoading(true); // Indicate loading start
    const payload = {
      orderType: selectedOrderType,
      contracts: selectedContracts,
    };

    try {
      if (!auth.currentUser) {
        alert("Please sign in again.");
        setLoading(false);
        return;
      }
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/submit_order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        alert(data.message || "Order submitted successfully!");
        setLastOrderType(selectedOrderType); // Store the type for status page display
        setPage("status");
        fetchTradeStatus(); // Fetch status immediately after potential fill
      } else {
        const errorText = await res.text();
        alert(`Order submission failed: ${res.status} - ${errorText}`);
      }
    } catch (err) {
      alert("Error submitting order.");
      console.error(err);
    }
    setLoading(false); // Indicate loading end
  };

  const handleCloseConfirm = () => {
    setShowConfirm(true);
  };

  const handleConfirmClose = async () => {
    if (!auth.currentUser) {
       alert("Please sign in again.");
       return;
    }
    setLoading(true);
    setShowConfirm(false);

    try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/close_order`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${token}` }
        });

        if (res.ok) {
            const data = await res.json();
            alert(data.message || "Close order submitted successfully.");
            // Reset state variables related to the trade
            setSelectedOrderType(null);
            setLastOrderType(null); // Clear the last order type
            setSelectedContracts(1);
            setOpenContracts(0); // Assume closure until next status fetch confirms
            setOpenPL(0);
            // setRealizedPL(0); // Keep realized P/L ? Depends on requirements
            // After successful close confirmation, navigate away
            setPage("connect"); // Go back to connect page per original request

        } else {
            const errorText = await res.text();
            alert(`Failed to close order: ${res.status} - ${errorText}`);
        }

    } catch (err) {
         alert("Error closing order.");
         console.error(err);
    } finally {
        setLoading(false);
    }
};

  // --- Render Functions (Updated Visuals) ---

  // Login Page
  const renderLoginPage = () => {
    // Styles for the circular progress indicators
    const circleStyle = (number) => ({
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #3B82F6, #A855F7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      fontWeight: '600',
      color: 'white',
      marginBottom: '1.5rem',
      boxShadow: '0 8px 16px rgba(59, 130, 246, 0.15)'
    });

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        boxSizing: 'border-box',
        position: 'relative',
        background: 'linear-gradient(180deg, #000 0%, #111 100%)',
        color: 'white',
        overflowX: 'hidden', // Prevent horizontal scroll
        width: '100%'
      }}>
        {/* Hero Section - Keep 32px margins */}
        <div style={{ 
          width: '100%',
          maxWidth: '1400px',
          padding: '120px 32px 80px', // Keep larger padding for hero
          textAlign: 'center',
          position: 'relative',
          boxSizing: 'border-box'
        }}>
          <h1 style={{ 
            fontSize: 'clamp(3rem, 8vw, 5rem)',
            fontWeight: 600,
            margin: '0 0 2rem 0',
            lineHeight: '1.1',
            background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.75) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            Trading Just Got Easier
          </h1>
          <p style={{ 
            fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '800px',
            margin: '0 auto 3rem',
            lineHeight: '1.5',
            padding: '0 32px' // Keep larger padding for hero text
          }}>
            Pick your entries and let our algorithms manage your trade till exit. Set and forget – powered by AI
          </p>
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '6rem'
          }}>
            <button
              onClick={signIn}
              style={{
                padding: '16px 32px',
                fontSize: '1.125rem',
                color: 'black',
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 6px rgba(255,255,255,0.1)',
                ':hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 8px rgba(255,255,255,0.2)'
                }
              }}
            >
              Get Started
            </button>
            <button
              style={{
                padding: '16px 32px',
                fontSize: '1.125rem',
                color: 'white',
                backgroundColor: 'transparent',
                border: '2px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'border-color 0.2s',
                ':hover': {
                  borderColor: 'rgba(255,255,255,0.4)'
                }
              }}
            >
              View Documentation
            </button>
          </div>
        </div>

        {/* What is TapToTrade Section */}
        <div style={{
          width: 'calc(100% - 48px)', // Account for margins on both sides
          maxWidth: '1400px',
          padding: '80px 24px',
          background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
          borderRadius: '32px',
          margin: '24px auto', // Center horizontally with consistent margins
          backdropFilter: 'blur(20px)',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 600,
            marginBottom: '2rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.75) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            What is TapToTrade?
          </h2>
          <p style={{ 
            fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
            lineHeight: '1.8',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 24px',
            textAlign: 'center'
          }}>
            You see the perfect trade entry but you're about to go into a 2 hour meeting and won't be able to manage your trade properly. 
            No worries, just enter the position with TapToTrade and our algorithms take over automating your exit strategy. 
            You pick the entry, direction and size and click buy. TapToTrade takes over executing your trades with systematic precision through your own Interactive Brokers account.
          </p>
        </div>

        {/* How It Works Section - Keep as is since it has correct margins */}
        <div style={{
          width: '100%',
          maxWidth: '1400px',
          padding: '120px 24px',
          position: 'relative'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 600,
            marginBottom: '4rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.75) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Trading Just Got Easier. Here's How:
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            padding: '0 24px'
          }}>
            {[
              {
                title: 'CONNECT SECURELY',
                description: "Create your TapToTrade account, then securely link your own Interactive Brokers account using IBKR's official authorization. Your brokerage credentials are never shared with us."
              },
              {
                title: 'PICK YOUR TRADE',
                description: "Choose your ticker (SPY or QQQ), select Call or Put, and define your position size. That's it."
              },
              {
                title: 'SET & FORGET',
                description: "TapToTrade's algorithms take command, automatically placing your entry order and then managing your pre-defined exit strategy."
              }
            ].map((step, index) => (
              <div key={index} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '1rem'
              }}>
                <div style={circleStyle(index + 1)}>{index + 1}</div>
                <h3 style={{ 
                  fontSize: '1.5rem',
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  color: 'white',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  {step.title}
                </h3>
                <p style={{ 
                  color: 'rgba(255,255,255,0.9)',
                  textAlign: 'center',
                  lineHeight: '1.6',
                  fontSize: '1.125rem',
                  margin: 0
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Built for S&P500 Section */}
        <div style={{
          width: 'calc(100% - 48px)', // Account for margins on both sides
          maxWidth: '1400px',
          padding: '80px 24px',
          background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
          borderRadius: '32px',
          margin: '24px auto', // Center horizontally with consistent margins
          backdropFilter: 'blur(20px)',
          boxSizing: 'border-box'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 600,
            marginBottom: '3rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.75) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Built for S&P500 and NASDAQ 0DTE Options Traders
          </h2>
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '0' // Remove padding here since we'll handle it in the grid
          }}>
            <p style={{ 
              fontSize: '1.25rem',
              lineHeight: '1.8',
              color: 'rgba(255,255,255,0.9)',
              textAlign: 'center',
              marginBottom: '3rem'
            }}>
              TapToTrade is designed specifically for active day traders who:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              width: '100%', // Ensure full width
              maxWidth: '1000px', // Match parent maxWidth
              margin: '0 auto' // Center the grid
            }}>
              {[
                'Focus on 0DTE (Zero Days to Expiration) options for SPY and QQQ',
                'Seek to eliminate emotional decision-making from their exit strategy',
                'Want a more disciplined, rules-based approach to managing trades',
                'Need to manage trades effectively even with limited screen time',
                'Use Interactive Brokers and want a secure way to enhance their trading workflow'
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '1.5rem',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <span style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3B82F6, #A855F7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1rem',
                    flexShrink: 0
                  }}>✓</span>
                  <p style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: '1.5'
                  }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Email Subscription Bar */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0, // Remove margin to extend to screen edges
          right: 0, // Remove margin to extend to screen edges
          background: 'rgba(0,0,0,0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          padding: '1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          boxSizing: 'border-box'
        }}>
          <div style={{
            maxWidth: '1000px',
            width: '100%',
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            margin: '0 auto',
            padding: '0 16px' // Add 16px padding to the content container
          }}>
            <p style={{
              margin: 0,
              fontSize: '1.125rem',
              color: 'white',
              flex: '1'
            }}>
              Be the First to Revolutionize Your Trading
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              flex: '2',
              maxWidth: '600px',
              width: '100%', // Ensure full width
              boxSizing: 'border-box' // Ensure padding is included in width
            }}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  flex: '1',
                  padding: '1rem 1.5rem',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  fontSize: '1rem',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  outline: 'none',
                  minWidth: 0 // Allow input to shrink below default size
                }}
              />
              <button style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #3B82F6, #A855F7)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                transition: 'transform 0.2s',
                ':hover': {
                  transform: 'translateY(-2px)'
                }
              }}>
                JOIN WAITLIST
              </button>
            </div>
          </div>
        </div>

        {/* Footer - Revert to 24px margins */}
        <footer style={{
          width: '100%',
          maxWidth: '1400px',
          padding: '2rem 24px', // Revert to original padding
          marginTop: '4rem',
          marginBottom: '100px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)' }}>
            © {new Date().getFullYear()} TapToTrade.ai
          </p>
          <div style={{ display: 'flex', gap: '2rem' }}>
            <a href="#" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', ':hover': { color: 'white' } }}>Privacy Policy</a>
            <a href="#" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', ':hover': { color: 'white' } }}>Terms of Service</a>
          </div>
        </footer>
      </div>
    );
  };

  // Connect Page
  const renderConnectPage = () => (
    <div style={{
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         justifyContent: 'center', // Center content vertically
         minHeight: '60vh', // Adjust height as needed
         padding: '40px 20px',
         fontFamily: 'sans-serif'
    }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 400, marginBottom: '1rem' }}>
          InteractiveBrokers
        </h2>
        <hr style={{ width: '80%', maxWidth: '300px', border: 0, borderTop: '1px solid #ccc', margin: '1rem 0 2rem 0' }} />
        <button
          onClick={handleConnect}
          disabled={loading}
          style={{
            padding: '15px 30px',
            fontSize: '1rem',
            backgroundColor: loading ? '#ccc' : (connected ? '#4caf50' : '#008080'), // Teal color, Gray when loading
            color: 'white',
            border: 'none',
            borderRadius: '8px', // Rounded corners
            cursor: 'pointer',
            minWidth: '200px', // Ensure minimum width
            textAlign: 'center'
          }}
        >
           {connected ? "Connected ✅" : loading ? "Connecting..." : "CONNECT TO BROKER"}
        </button>
    </div>
  );

  // Trade Page
  const renderTradePage = () => {
     const commonButtonStyle = {
        padding: '16px 24px',
        margin: '0 10px 15px 10px', // Add horizontal margin
        borderRadius: '8px',
        fontSize: '18px',
        fontWeight: 'bold',
        border: '2px solid transparent', // Base border
        cursor: 'pointer',
        width: 'calc(50% - 20px)', // Adjust width for two buttons side-by-side with margin
        boxSizing: 'border-box'
     };

     const callButtonStyle = {
         ...commonButtonStyle,
         backgroundColor: selectedOrderType === 'CALL' ? '#3b82f6' : '#E5E7EB', // Blue when selected, Gray otherwise
         color: selectedOrderType === 'CALL' ? 'white' : '#6B7280',
         border: selectedOrderType === 'CALL' ? '2px solid #1d4ed8' : '2px solid #D1D5DB',
     };

     const putButtonStyle = {
         ...commonButtonStyle,
         backgroundColor: selectedOrderType === 'PUT' ? '#ef4444' : '#E5E7EB', // Red when selected, Gray otherwise
         color: selectedOrderType === 'PUT' ? 'white' : '#6B7280',
         border: selectedOrderType === 'PUT' ? '2px solid #b91c1c' : '2px solid #D1D5DB',
     };

     return (
       <div style={{ fontFamily: 'sans-serif' }}>
         <StaticConnectedBar />
         <div style={{ padding: '20px' }}>
           <h2 style={{ fontWeight: 400, marginBottom: '1rem' }}>Trade</h2>
           <p style={{ color: '#555', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
             SPY | NYSE ARCA <span style={{ marginLeft: '5px' }}></span>
           </p>
           <hr style={{ width: '100%', border: 0, borderTop: '1px solid #ccc', margin: '1rem 0 2rem 0' }} />

           <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
             <button onClick={() => setSelectedOrderType("CALL")} style={callButtonStyle}>
               CALL
             </button>
             <button onClick={() => setSelectedOrderType("PUT")} style={putButtonStyle}>
               PUT
             </button>
           </div>

           <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px' }}>
              <label style={{ color: '#333' }}>Number of Contracts</label>
              {/* Simple select styling - more complex requires CSS classes */}
              <select
                  value={selectedContracts}
                  onChange={(e) => setSelectedContracts(parseInt(e.target.value))}
                  style={{
                     fontSize: '1rem',
                     padding: '8px 12px',
                     borderRadius: '6px',
                     border: '1px solid #ccc',
                     minWidth: '60px'
                 }}
               >
                 {[...Array(10)].map((_, i) => (
                   <option key={i + 1} value={i + 1}>{i + 1}</option>
                 ))}
              </select>
           </div>

           <button
               onClick={handleSubmitOrder}
               disabled={loading}
               style={{
                    display: 'block',
                    width: 'calc(100% - 20px)', // Full width minus padding
                    margin: '0 10px', // Horizontal margin
                    padding: '15px 20px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: loading ? '#666' : 'black', // Darker gray when loading
                    border: 'none',
                    borderRadius: '30px', // Pill shape
                    cursor: 'pointer',
                    textAlign: 'center'
                }}
            >
             {loading ? "Submitting..." : "Submit Order"}
           </button>
         </div>
       </div>
     );
  };

  // Status Page
  const renderStatusPage = () => (
    <div style={{ fontFamily: 'sans-serif', backgroundColor: '#A0E6FF', minHeight: '100vh' }}>
      <StaticConnectedBar />
      <div style={{ padding: '30px 20px' }}>
        <div style={{ marginBottom: '30px' }}>
           <p style={{ color: '#333', fontSize: '1rem', margin: '0 0 5px 0' }}>Open P/L</p>
           <p style={{ color: 'black', fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>
                ${openPL.toFixed(2)}
           </p>
        </div>
        <div style={{ marginBottom: '40px' }}>
           <p style={{ color: '#333', fontSize: '1rem', margin: '0 0 5px 0' }}>Realized P/L</p>
           <p style={{ color: 'black', fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
                ${realizedPL.toFixed(2)}
           </p>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '20px 20px 0 0', padding: '30px 20px', position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <p style={{ fontWeight: 'bold', color: '#333', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              Open Positions <span style={{ marginLeft: '5px' }}></span>
            </p>
            <hr style={{ width: '100%', border: 0, borderTop: '1px solid #ccc', margin: '1rem 0 2rem 0' }} />

            {/* Only show position details if contracts > 0 */}
            {openContracts > 0 && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                         <div style={{display: 'flex', alignItems: 'center'}}>
                           <span style={{display: 'inline-block', width: '12px', height: '12px', backgroundColor: 'black', borderRadius: '50%', marginRight: '10px'}}></span>
                           <span>Trade Direction</span>
                         </div>
                         <span style={{ fontWeight: 'bold', color: lastOrderType === 'CALL' ? '#16a34a' : (lastOrderType === 'PUT' ? '#ef4444' : 'black') }}>
                              {lastOrderType === 'CALL' ? 'LONG CALLS' : (lastOrderType === 'PUT' ? 'LONG PUTS' : 'N/A')}
                         </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2.5rem' }}>
                         <div style={{display: 'flex', alignItems: 'center'}}>
                            <span style={{display: 'inline-block', width: '12px', height: '12px', backgroundColor: 'black', borderRadius: '50%', marginRight: '10px'}}></span>
                            <span>Open Contracts</span>
                         </div>
                         <span style={{ fontWeight: 'bold' }}>{openContracts}</span>
                    </div>
                </>
            )}
             {/* Show message if no open contracts */}
             {openContracts <= 0 && (
                <p style={{textAlign: 'center', color: '#555', marginBottom: '2.5rem'}}>No Open Positions</p>
             )}


            <button
                onClick={handleCloseConfirm}
                disabled={loading || openContracts <= 0} // Disable if loading or no contracts
                style={{
                    display: 'block',
                    width: '100%',
                    padding: '15px 20px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: (loading || openContracts <= 0) ? '#666' : 'black', // Gray if disabled
                    border: 'none',
                    borderRadius: '30px', // Pill shape
                    cursor: (loading || openContracts <= 0) ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    marginBottom: '20px' // Add margin for confirmation
                }}
            >
             {loading ? "Processing..." : "Close Position"}
            </button>

            {/* Confirmation Dialog (Keep existing logic, adjust styles slightly maybe) */}
            {showConfirm && (
              <div style={{ marginTop: "20px", padding: "15px", border: "1px solid #ccc", borderRadius: "10px", backgroundColor: "#f9f9f9", textAlign: 'center' }}>
                <p style={{margin: '0 0 15px 0', fontWeight: 'bold'}}>Confirm Close Position?</p>
                <button
                  onClick={handleConfirmClose}
                  disabled={loading}
                  style={{
                    backgroundColor: "#ff9800", // Orange color
                    color: "white",
                    padding: "10px 20px",
                    fontSize: "14px",
                    border: "none",
                    borderRadius: "20px", // Pill shape
                    cursor: "pointer",
                    minWidth: '150px'
                  }}
                >
                  Confirm Close
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );

  // --- Page Routing ---
  if (!user) {
     // If not logged in, always show login page
     return renderLoginPage();
  }

  // If logged in, show page based on state
  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}> {/* Apply base font smoothing */}
      {page === "connect" && renderConnectPage()}
      {page === "trade" && renderTradePage()}
      {page === "status" && renderStatusPage()}
      {/* Add a fallback or loading state if needed */}
      {page === "login" && user && renderConnectPage()} {/* Edge case: If user logs in but page state is slow, show connect */}
    </div>
  );
}

export default App;